import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { CalendarRange, ChevronLeft, ChevronRight, List, Plus, Search, X } from 'lucide-react-native';
import { useSQLiteContext } from 'expo-sqlite';
import { ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';

import { useConfirm, useNotice } from '@/components/ConfirmDialog';
import { useConfetti } from '@/components/ConfettiOverlay';
import { InfiniteFooter } from '@/components/InfiniteFooter';
import { SectionLabel } from '@/components/home/SectionLabel';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { goTo } from '@/lib/nav';
import { useInfiniteWindow } from '@/hooks/use-infinite-window';
import { useDataRevision } from '@/lib/data-revision';
import { registerScrollToTop } from '@/lib/scroll-bus';
import {
  type ActiveTask,
  completeTask,
  deleteTask,
  getActiveProperty,
  getActiveTasks,
  getAllSeasonsProgress,
  getCompletedTasks,
  hardDeleteTask,
  restoreHardDeletedTask,
  setTaskDueAt,
  type SeasonStat,
  skipTask,
  snoozeTask,
  uncompleteTask,
  undeleteTask,
} from '@/lib/tasks';
import { offerUndo } from '@/lib/undo';
import { ReschedulePicker } from '@/components/home/ReschedulePicker';
import { CompactTaskRow } from '@/components/home/CompactTaskRow';
import { TaskActionsSheet } from '@/components/TaskActionsSheet';
import { webNoOutline } from '@/lib/web-style';

type SeasonName = 'spring' | 'summer' | 'fall' | 'winter';
const SEASON_LABEL: Record<SeasonName, string> = {
  spring: 'Spring', summer: 'Summer', fall: 'Fall', winter: 'Winter',
};
// Per-season accent colors — moss for spring (growth), honey for summer
// (warmth), clay for fall (turning leaves), forestLight for winter
// (cold mute). Used to tint the chip header dot + progress bar.
const SEASON_TINT: Record<SeasonName, string> = {
  spring: Palette.moss,
  summer: Palette.honey,
  fall: Palette.clay,
  winter: Palette.forestLight,
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default function Schedule() {
  const db = useSQLiteContext();
  const router = useRouter();
  const notice = useNotice();
  const confirm = useConfirm();
  const { fire } = useConfetti();
  const scheduleScrollRef = useRef<ScrollView | null>(null);
  const resetScroll = useCallback(() => {
    scheduleScrollRef.current?.scrollTo({ y: 0, animated: false });
  }, []);
  useEffect(() => registerScrollToTop(resetScroll), [resetScroll]);
  const [tasks, setTasks] = useState<ActiveTask[]>([]);
  const [completed, setCompleted] = useState<ActiveTask[]>([]);
  // View mode toggle: list = grouped-by-month task list (default),
  // monthly = calendar grid with tap-to-filter day cells. Persists in
  // component state only — the URL filter (date) is what actually
  // scopes the visible tasks, so switching modes is a pure UI change.
  const [viewMode, setViewMode] = useState<'list' | 'monthly'>('list');
  // Which month the monthly view is currently showing. Defaults to
  // current month; user navigates with prev/next chevrons in the
  // calendar header.
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [seasonStats, setSeasonStats] = useState<Record<SeasonName, SeasonStat>>({
    spring: { done: 0, total: 0, pct: 0 },
    summer: { done: 0, total: 0, pct: 0 },
    fall:   { done: 0, total: 0, pct: 0 },
    winter: { done: 0, total: 0, pct: 0 },
  });
  // Query params drive every filter on this page:
  //   ?season=spring|summer|fall|winter  scopes to a single season
  //   ?date=YYYY-MM-DD                   scopes to a single calendar day
  //   ?overdue=1                         scopes to overdue (active + past due)
  //   ?completed=1                       switches the source set to completed
  //                                       tasks (replaces the old "Past tasks"
  //                                       tool — same data, surfaced here as
  //                                       a chip).
  // All filters are mutually-clearable — each chip's X clears its own
  // param so users can layer / un-layer freely.
  const params = useLocalSearchParams<{
    season?: string;
    date?: string;
    overdue?: string;
    completed?: string;
  }>();
  const seasonFilter = (params.season?.toLowerCase() as SeasonName | undefined);
  const validSeason: SeasonName | null =
    seasonFilter && (['spring', 'summer', 'fall', 'winter'] as const).includes(seasonFilter)
      ? seasonFilter : null;
  const dateFilter = typeof params.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(params.date)
    ? params.date
    : null;
  const overdueFilter = params.overdue === '1';
  const completedFilter = params.completed === '1';

  // Local-only search filter. Not a URL param because the user is
  // unlikely to deep-link a query and re-renders would keep stealing
  // focus from the input on every keystroke.
  const [search, setSearch] = useState('');
  const searchTerm = search.trim().toLowerCase();
  // Pretty-format the date param for the filter chip / subhead — locale-aware
  // weekday + month/day, e.g. "Mon, May 19".
  const dateFilterLabel = useMemo(() => {
    if (!dateFilter) return null;
    const [y, m, d] = dateFilter.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  }, [dateFilter]);

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    if (!prop) {
      setTasks([]);
      setCompleted([]);
      return;
    }
    const [ts, cs, sp] = await Promise.all([
      getActiveTasks(db, prop.id, 200),
      // Completed tasks for the list view's grouping. 500 covers a heavy
      // user's full-year history.
      getCompletedTasks(db, prop.id, 500),
      // Per-season stats come from the same shared SQL the Home season
      // ring uses, so the two views can never drift apart.
      getAllSeasonsProgress(db, prop.id),
    ]);
    setTasks(ts);
    setCompleted(cs);
    setSeasonStats(sp);
  }, [db]);

  useFocusEffect(
    useCallback(() => {
      fetch();
      resetScroll();
    }, [fetch, resetScroll])
  );

  // Refetch on any DB write (most importantly: home-switch via the
  // HomeSwitcher → setActiveProperty → bumpDataRevision pipeline). Keeps
  // the Schedule list in lockstep with the active home without forcing
  // the user to navigate away and back.
  const revision = useDataRevision();
  useEffect(() => {
    void fetch();
  }, [revision, fetch]);

  const filtered = useMemo(() => {
    // Source-set switch: completed=1 swaps active tasks for completed
    // ones. Season / date / overdue layers still apply on top so users
    // can ask "what did I finish in spring?" etc.
    let out = completedFilter ? completed : tasks;
    if (validSeason) {
      // Astronomical season boundaries (rough — close enough for filter).
      const y = new Date().getFullYear();
      const SEASON_BOUNDS: Record<SeasonName, [number, number]> = {
        spring: [new Date(y, 2, 20).getTime(), new Date(y, 5, 21).getTime()],
        summer: [new Date(y, 5, 21).getTime(), new Date(y, 8, 22).getTime()],
        fall:   [new Date(y, 8, 22).getTime(), new Date(y, 11, 21).getTime()],
        winter: [new Date(y, 11, 21).getTime(), new Date(y + 1, 2, 20).getTime()],
      };
      const [from, to] = SEASON_BOUNDS[validSeason];
      out = out.filter((t) => {
        // Season tag match (task.season is 'spring'|'summer'|'fall'|'winter'|'any')
        if (t.season === validSeason) return true;
        // Or the task is due inside the season window (covers 'any' tasks
        // the planner scheduled for that period).
        const d = t.due_at ?? 0;
        return d >= from && d < to;
      });
    }
    // Single-day scope — selected from the home calendar peek. Limits
    // the list to tasks whose due_at falls on that calendar day.
    if (dateFilter) {
      const [y, m, d] = dateFilter.split('-').map(Number);
      const dayStart = new Date(y, m - 1, d).getTime();
      const dayEnd = dayStart + 24 * 60 * 60 * 1000;
      out = out.filter((t) => {
        const dt = t.due_at ?? 0;
        return dt >= dayStart && dt < dayEnd;
      });
    }
    // Overdue scope — anything still active with a due_at before today.
    // Same definition CalendarPeek uses for its OVERDUE chip count so
    // the number on Home matches the count after navigating here.
    if (overdueFilter) {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      out = out.filter((t) => t.due_at != null && t.due_at < startOfToday);
    }
    // Search filter applies on top of every other slice. Matches
    // task title (the obvious thing) AND the resolved system label so
    // typing "dryer" surfaces vent cleanings, lint trap tasks, etc.
    if (searchTerm) {
      out = out.filter((t) => {
        const haystack = [
          t.title,
          t.system_nickname,
          t.system_kind,
          t.system_subtype,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(searchTerm);
      });
    }
    return out;
  }, [tasks, completed, completedFilter, validSeason, dateFilter, overdueFilter, searchTerm]);

  // Render-window the filtered set before grouping. With the active-
  // task cap raised to 200 at the SQL layer, painting every TaskRow
  // (each has its own animated check + overdue badge math) hurts
  // low-end Android. Bucketing happens after windowing so partial
  // loads still show clean monthly section headers.
  const win = useInfiniteWindow(filtered, { pageSize: 25, initialSize: 20 });

  const grouped = useMemo(() => {
    const map = new Map<string, ActiveTask[]>();
    for (const t of win.visible) {
      const due = new Date(t.due_at ?? Date.now());
      const key = `${due.getFullYear()}-${due.getMonth()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    return Array.from(map.entries()).map(([key, items]) => {
      const [year, month] = key.split('-').map(Number);
      return {
        key,
        label: `${MONTHS[month]} ${year}`,
        items,
      };
    });
  }, [win.visible]);

  const onComplete = useCallback(
    async (taskId: string, point: { x: number; y: number }) => {
      fire(point.x, point.y, 9);
      await completeTask(db, taskId);
      fetch();
      offerUndo({
        message: 'Task marked done',
        undo: () => uncompleteTask(db, taskId),
        onUndone: () => fetch(),
      });
    },
    [db, fetch, fire]
  );

  const onSkip = useCallback(
    async (taskId: string) => {
      await skipTask(db, taskId);
      fetch();
      offerUndo({
        message: 'Task skipped',
        undo: () => uncompleteTask(db, taskId),
        onUndone: () => fetch(),
      });
    },
    [db, fetch]
  );

  // Context menu (long-press / right-click on a row).
  const [menuTask, setMenuTask] = useState<ActiveTask | null>(null);
  const onMenuDelete = useCallback(
    async (task: ActiveTask) => {
      await deleteTask(db, task.id);
      fetch();
      offerUndo({
        message: 'Task deleted',
        undo: () => undeleteTask(db, task.id),
        onUndone: () => fetch(),
      });
    },
    [db, fetch]
  );
  const onMenuDeleteForever = useCallback(
    async (task: ActiveTask) => {
      const ok = await confirm({
        title: 'Delete forever?',
        message:
          `"${task.title}" and its entire completion history will be ` +
          `permanently removed — costs logged on it disappear from ` +
          `service history too. This cannot be undone.`,
        destructive: true,
        confirmLabel: 'Delete forever',
      });
      if (!ok) return;
      const snap = await hardDeleteTask(db, task.id);
      fetch();
      if (snap) {
        offerUndo({
          message: 'Task permanently deleted',
          undo: () => restoreHardDeletedTask(db, snap),
          onUndone: () => fetch(),
        });
      }
    },
    [db, fetch, confirm]
  );

  /** Redo / undo handler for the Completed view — moves the task back
   *  into the active queue by stripping its latest completion record. */
  const onRedo = useCallback(
    async (taskId: string) => {
      await uncompleteTask(db, taskId);
      fetch();
    },
    [db, fetch]
  );

  // Reschedule picker — lifted from Today so the Schedule list gets
  // the same skip-or-reschedule affordance the new CompactTaskRow
  // expects. Selecting a delta delegates to snoozeTask, then refresh.
  const [reschedulingTask, setReschedulingTask] = useState<
    { id: string; title: string } | null
  >(null);
  const onReschedule = useCallback((taskId: string, title: string) => {
    setReschedulingTask({ id: taskId, title });
  }, []);
  const confirmReschedule = useCallback(
    async (days: number) => {
      if (!reschedulingTask) return;
      const taskId = reschedulingTask.id;
      const { prevDueAt } = await snoozeTask(db, taskId, days);
      setReschedulingTask(null);
      fetch();
      offerUndo({
        message: 'Task rescheduled',
        undo: () => setTaskDueAt(db, taskId, prevDueAt),
        onUndone: () => fetch(),
      });
    },
    [db, fetch, reschedulingTask]
  );

  return (
    <View style={styles.container}>
      <ScrollView
        ref={scheduleScrollRef}
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
        onScroll={win.onScroll}
        onLayout={win.onLayout}
        scrollEventThrottle={16}>
        <View style={styles.header}>
          <View style={styles.titleRow}>
            <Text style={styles.h1}>Schedule</Text>
            <View style={styles.titleIcons}>
              <ScheduleIconBtn
                Icon={Plus}
                label="Add task"
                accessibilityLabel="Add a custom task"
                onPress={() => goTo(router, '/custom-task/none')}
              />
              <ScheduleIconBtn
                Icon={List}
                label="List"
                accessibilityLabel="List view"
                active={viewMode === 'list'}
                onPress={() => setViewMode('list')}
              />
              <ScheduleIconBtn
                Icon={CalendarRange}
                label="Monthly"
                accessibilityLabel="Monthly calendar view"
                active={viewMode === 'monthly'}
                onPress={() => setViewMode('monthly')}
              />
            </View>
          </View>
          <Text style={styles.sub}>
            {filtered.length} task{filtered.length === 1 ? '' : 's'}
            {completedFilter
              ? ' completed'
              : overdueFilter
              ? ' overdue — let’s catch up'
              : dateFilterLabel
              ? ` on ${dateFilterLabel}`
              : validSeason
              ? ` in ${SEASON_LABEL[validSeason]}`
              : ' ahead, grouped by month'}
          </Text>

          {/*
            Status filter row — three quick scope toggles + an optional
            date chip when arriving from a calendar tap. Sits ABOVE the
            seasons so the most common slice ("upcoming / overdue /
            completed") is the first decision a user sees on this page.
            Each chip is tap-to-toggle; tapping an active chip clears
            its param and falls back to the default upcoming view.
          */}
          <View style={styles.statusRow}>
            <Pressable
              onPress={() =>
                router.setParams({
                  overdue: undefined,
                  completed: undefined,
                  date: undefined,
                })
              }
              style={({ pressed }) => [
                styles.statusChip,
                !overdueFilter && !completedFilter && !dateFilter && styles.statusChipActive,
                pressed && { opacity: 0.85 },
              ]}>
              <Text
                style={[
                  styles.statusChipText,
                  !overdueFilter && !completedFilter && !dateFilter && styles.statusChipTextActive,
                ]}>
                Upcoming
              </Text>
            </Pressable>
            <Pressable
              onPress={() =>
                router.setParams({
                  overdue: overdueFilter ? undefined : '1',
                  completed: undefined,
                })
              }
              style={({ pressed }) => [
                styles.statusChip,
                overdueFilter && styles.statusChipActiveClay,
                pressed && { opacity: 0.85 },
              ]}>
              <Text
                style={[
                  styles.statusChipText,
                  overdueFilter && styles.statusChipTextActive,
                ]}>
                Overdue
              </Text>
            </Pressable>
            <Pressable
              onPress={() =>
                router.setParams({
                  completed: completedFilter ? undefined : '1',
                  overdue: undefined,
                })
              }
              style={({ pressed }) => [
                styles.statusChip,
                completedFilter && styles.statusChipActive,
                pressed && { opacity: 0.85 },
              ]}>
              <Text
                style={[
                  styles.statusChipText,
                  completedFilter && styles.statusChipTextActive,
                ]}>
                Completed
              </Text>
            </Pressable>
            {dateFilter ? (
              <Pressable
                onPress={() => router.setParams({ date: undefined })}
                style={({ pressed }) => [styles.statusChip, styles.statusChipActive, pressed && { opacity: 0.85 }]}>
                <Text style={[styles.statusChipText, styles.statusChipTextActive]}>{dateFilterLabel}</Text>
                <X size={11} color={Palette.cream} strokeWidth={2.4} />
              </Pressable>
            ) : null}
          </View>

          {/*
            Search input — narrows whatever the active filter is to
            tasks whose title or system label contains the query. Local
            state, not URL-driven, because typing in a URL-bound input
            re-renders on every keystroke and steals focus on web.
          */}
          <View style={styles.searchWrap}>
            <Search size={14} color={Palette.inkFaint} strokeWidth={1.8} />
            <TextInput
              value={search}
              onChangeText={setSearch}
              placeholder="Search tasks…"
              placeholderTextColor={Palette.inkFaint}
              autoCorrect={false}
              autoCapitalize="none"
              style={[styles.searchInput, webNoOutline]}
            />
            {search ? (
              <Pressable
                onPress={() => setSearch('')}
                hitSlop={8}
                accessibilityLabel="Clear search">
                <X size={13} color={Palette.inkSoft} strokeWidth={2.2} />
              </Pressable>
            ) : null}
          </View>

          {/*
            Four season chips, compact + side-by-side. Each chip shows
            the season label and its done/total % so users can pick a
            chunk of work to focus on. flexBasis was removed so they
            always lay out in a single row on every viewport — the chip
            is narrow enough (label + pct + bar) to fit a 320px phone
            without wrapping.
          */}
          {viewMode === 'list' ? (
            <View style={styles.seasonRow}>
              {(['spring', 'summer', 'fall', 'winter'] as SeasonName[]).map((s) => {
                const active = validSeason === s;
                const stats = seasonStats[s];
                const tint = SEASON_TINT[s];
                return (
                  <Pressable
                    key={s}
                    onPress={() => router.setParams({ season: active ? undefined : s })}
                    style={({ pressed }) => [
                      styles.seasonChip,
                      active && styles.seasonChipActive,
                      active && { borderColor: tint, backgroundColor: tint },
                      pressed && { opacity: 0.85 },
                    ]}>
                    <Text style={[styles.seasonChipName, active && styles.seasonChipNameActive]}>
                      {SEASON_LABEL[s]}
                    </Text>
                    <Text
                      style={[styles.seasonChipPct, active && styles.seasonChipPctActive]}>
                      {stats.pct}%
                    </Text>
                    <View style={styles.seasonBarTrack}>
                      <View
                        style={[
                          styles.seasonBarFill,
                          { width: `${Math.min(100, Math.max(0, stats.pct))}%` },
                          { backgroundColor: active ? Palette.cream : tint },
                        ]}
                      />
                    </View>
                  </Pressable>
                );
              })}
            </View>
          ) : null}
        </View>

        {viewMode === 'monthly' ? (
          <MonthlyCalendar
            year={calendarMonth.year}
            month={calendarMonth.month}
            tasks={tasks}
            selectedDate={dateFilterLabel ? (params.date as string | undefined) ?? null : null}
            onPrev={() =>
              setCalendarMonth((c) =>
                c.month === 0
                  ? { year: c.year - 1, month: 11 }
                  : { year: c.year, month: c.month - 1 }
              )
            }
            onNext={() =>
              setCalendarMonth((c) =>
                c.month === 11
                  ? { year: c.year + 1, month: 0 }
                  : { year: c.year, month: c.month + 1 }
              )
            }
            onToday={() => {
              const d = new Date();
              setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
            }}
            onPickDate={(iso) => {
              // Tapping a date filters the list below to that day.
              // We don't auto-switch to list mode — let the user keep
              // seeing the calendar so they can scan the rest of the
              // month after acting on a single day.
              router.setParams({ date: iso });
            }}
          />
        ) : null}

        {grouped.length === 0 ? (
          <View style={styles.empty}>
            <Text style={styles.emptyTitle}>Nothing scheduled</Text>
            <Text style={styles.emptyBody}>
              Finish onboarding or wait for the plan to populate — you&apos;re all set
              once tasks land here.
            </Text>
          </View>
        ) : (
          <>
            {grouped.map((g) => (
              <View key={g.key}>
                <SectionLabel title={g.label} hint={`${g.items.length} task${g.items.length === 1 ? '' : 's'}`} />
                <View style={styles.compactList}>
                  {g.items.map((t) => (
                    <CompactTaskRow
                      key={t.id}
                      task={t}
                      onComplete={(p) => onComplete(t.id, p)}
                      // Completed view swaps check + skip + reschedule
                      // for a single Redo button; branch by passing
                      // mutually exclusive handlers — CompactTaskRow
                      // renders one mode or the other.
                      onSkip={completedFilter ? undefined : () => onSkip(t.id)}
                      onReschedule={
                        completedFilter ? undefined : () => onReschedule(t.id, t.title)
                      }
                      onRedo={completedFilter ? () => onRedo(t.id) : undefined}
                      onMenu={completedFilter ? undefined : setMenuTask}
                    />
                  ))}
                </View>
              </View>
            ))}
            <InfiniteFooter hasMore={win.hasMore} total={win.total} loadMore={win.loadMore} noun="task" />
          </>
        )}
      </ScrollView>
      <ReschedulePicker
        task={reschedulingTask}
        onCancel={() => setReschedulingTask(null)}
        onPick={confirmReschedule}
      />
      <TaskActionsSheet
        task={menuTask}
        onClose={() => setMenuTask(null)}
        onOpen={() => menuTask && router.push(`/task/${menuTask.id}` as never)}
        onComplete={() => menuTask && onComplete(menuTask.id, { x: 200, y: 300 })}
        onSkip={() => menuTask && onSkip(menuTask.id)}
        onReschedule={() => menuTask && onReschedule(menuTask.id, menuTask.title)}
        onDelete={() => menuTask && onMenuDelete(menuTask)}
        onDeleteForever={() => menuTask && onMenuDeleteForever(menuTask)}
      />
    </View>
  );
}

/**
 * Compact monthly calendar grid for the Schedule's "Monthly" view.
 * Header is month/year with prev/next/today buttons; body is a 7×N
 * grid of date cells (Sun-Sat). Each cell shows the day number plus a
 * tiny count badge if any tasks land on that date. Tapping a cell
 * filters the list below to that date via router.setParams. Today is
 * outlined; the selected date (if any) is filled.
 *
 * No drag-drop — that's a much bigger undertaking and the user
 * explicitly scrapped it for now. The grid is read-only navigation.
 */
function MonthlyCalendar({
  year,
  month,
  tasks,
  selectedDate,
  onPrev,
  onNext,
  onToday,
  onPickDate,
}: {
  year: number;
  month: number;
  tasks: ActiveTask[];
  selectedDate: string | null;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onPickDate: (iso: string) => void;
}) {
  // Build a count of tasks per ISO date for fast O(1) lookup while
  // we walk the 6-week grid below.
  const taskCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of tasks) {
      if (t.due_at == null) continue;
      const d = new Date(t.due_at);
      if (d.getFullYear() !== year || d.getMonth() !== month) continue;
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      counts.set(iso, (counts.get(iso) ?? 0) + 1);
    }
    return counts;
  }, [tasks, year, month]);

  // Build a 6×7 grid (always 42 cells — handles every month layout
  // including 31-day months starting Sat). Leading + trailing days
  // from the prev/next month are rendered faded.
  const cells: { date: Date; inMonth: boolean }[] = useMemo(() => {
    const first = new Date(year, month, 1);
    const leading = first.getDay(); // 0 = Sun
    const start = new Date(year, month, 1 - leading);
    const out: { date: Date; inMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      out.push({ date: d, inMonth: d.getMonth() === month });
    }
    return out;
  }, [year, month]);

  const today = new Date();
  const isToday = (d: Date) =>
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  const monthLabel = MONTHS[month];

  const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <View style={calStyles.wrap}>
      <View style={calStyles.header}>
        <Pressable
          onPress={onPrev}
          hitSlop={8}
          accessibilityLabel="Previous month"
          style={({ pressed }) => [calStyles.navBtn, pressed && { opacity: 0.6 }]}>
          <ChevronLeft size={18} color={Palette.forest} strokeWidth={2} />
        </Pressable>
        <Pressable
          onPress={onToday}
          accessibilityLabel="Jump to today"
          style={({ pressed }) => [calStyles.title, pressed && { opacity: 0.7 }]}>
          <Text style={calStyles.titleText}>
            {monthLabel} <Text style={calStyles.titleYear}>{year}</Text>
          </Text>
        </Pressable>
        <Pressable
          onPress={onNext}
          hitSlop={8}
          accessibilityLabel="Next month"
          style={({ pressed }) => [calStyles.navBtn, pressed && { opacity: 0.6 }]}>
          <ChevronRight size={18} color={Palette.forest} strokeWidth={2} />
        </Pressable>
      </View>
      <View style={calStyles.dowRow}>
        {dayHeaders.map((h, i) => (
          <Text key={`${h}-${i}`} style={calStyles.dowText}>
            {h}
          </Text>
        ))}
      </View>
      <View style={calStyles.grid}>
        {cells.map(({ date, inMonth }, i) => {
          const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          const count = taskCounts.get(iso) ?? 0;
          const selected = selectedDate === iso;
          const todayCell = isToday(date);
          return (
            <Pressable
              key={i}
              onPress={() => onPickDate(iso)}
              accessibilityLabel={`${date.toDateString()}, ${count} task${count === 1 ? '' : 's'}`}
              style={({ pressed }) => [
                calStyles.cell,
                selected && calStyles.cellSelected,
                todayCell && !selected && calStyles.cellToday,
                pressed && { opacity: 0.75 },
              ]}>
              <Text
                style={[
                  calStyles.cellDay,
                  !inMonth && calStyles.cellDayFaded,
                  selected && calStyles.cellDaySelected,
                ]}>
                {date.getDate()}
              </Text>
              {count > 0 ? (
                <View
                  style={[
                    calStyles.dot,
                    selected && { backgroundColor: Palette.cream },
                  ]}>
                  <Text
                    style={[
                      calStyles.dotText,
                      selected && { color: Palette.forest },
                    ]}>
                    {count > 9 ? '9+' : count}
                  </Text>
                </View>
              ) : null}
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const calStyles = StyleSheet.create({
  wrap: {
    marginTop: Spacing.three,
    marginHorizontal: 22,
    padding: 14,
    backgroundColor: Palette.card,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  navBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Palette.sand,
  },
  title: { paddingHorizontal: 12, paddingVertical: 4, borderRadius: 8 },
  titleText: {
    fontFamily: Fonts.serif,
    fontSize: 18,
    color: Palette.ink,
    letterSpacing: -0.2,
  },
  titleYear: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.inkSoft,
  },
  dowRow: {
    flexDirection: 'row',
    paddingHorizontal: 2,
    marginBottom: 4,
  },
  dowText: {
    flex: 1,
    textAlign: 'center',
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.inkFaint,
    letterSpacing: 0.6,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  cell: {
    width: `${100 / 7}%`,
    aspectRatio: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    gap: 2,
  },
  cellToday: {
    borderWidth: 1.5,
    borderColor: Palette.forest,
  },
  cellSelected: {
    backgroundColor: Palette.forest,
  },
  cellDay: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.ink,
  },
  cellDayFaded: {
    color: Palette.inkFaint,
    opacity: 0.55,
  },
  cellDaySelected: {
    color: Palette.cream,
  },
  dot: {
    minWidth: 16,
    height: 14,
    borderRadius: 7,
    paddingHorizontal: 4,
    backgroundColor: Palette.honeyDeep,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.cream,
    lineHeight: 11,
  },
});

/**
 * Schedule-page action icon. Same vertical icon+label stack pattern as
 * My Home's TitleIconBtn — kept duplicated here rather than extracted
 * to a shared component to avoid a Wave-19-style refactor mid-Wave; we
 * can hoist if/when a third caller appears.
 */
function ScheduleIconBtn({
  Icon,
  label,
  accessibilityLabel,
  onPress,
  active,
}: {
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  label: string;
  accessibilityLabel: string;
  onPress: () => void;
  active?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}
      hitSlop={6}
      style={({ pressed }) => [
        styles.titleIconStack,
        pressed && { transform: [{ scale: 0.94 }], opacity: 0.85 },
      ]}>
      <View style={[styles.titleIconBtn, active && styles.titleIconBtnActive]}>
        <Icon
          size={17}
          color={active ? Palette.cream : Palette.forest}
          strokeWidth={1.9}
        />
      </View>
      <Text style={styles.titleIconLabel} numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Palette.sand,
  },
  header: {
    paddingHorizontal: 22,
    paddingTop: Spacing.three,
  },
  // Title + icon-menu row. Mirrors My Home so the affordance is
  // consistent across pages.
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  titleIcons: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 4,
  },
  titleIconStack: {
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 2,
  },
  titleIconBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Active state = "this is the current view." Filled forest so it
  // reads as the selected toggle in the View pair (List / Monthly).
  titleIconBtnActive: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  titleIconLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    lineHeight: 11,
    color: Palette.forest,
    letterSpacing: 0.2,
    maxWidth: 60,
    textAlign: 'center',
  },
  h1: {
    fontFamily: Fonts.serif,
    fontSize: 27,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  sub: {
    marginTop: 3,
    fontSize: 13,
    color: Palette.inkSoft,
    fontFamily: Fonts.sansMedium,
  },
  empty: {
    paddingHorizontal: 32,
    paddingVertical: 60,
    alignItems: 'center',
  },
  emptyTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 18,
    color: Palette.ink,
    marginBottom: 8,
  },
  emptyBody: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    textAlign: 'center',
    lineHeight: 20,
  },
  list: {
    paddingHorizontal: 18,
    gap: 9,
  },
  // Compact list rhythm — tighter gap matched to CompactTaskRow density.
  compactList: {
    paddingHorizontal: 18,
    gap: 6,
  },
  // ---- Status chip row ----------------------------------------------------
  // Sits between the page subtitle and the seasons. Compact pill chips
  // with a single clear active state. flex:1 across so they always fit
  // in one row on mobile.
  statusRow: {
    marginTop: 10,
    flexDirection: 'row',
    gap: 6,
  },
  statusChip: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderRadius: Radius.pill,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  statusChipActive: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  // Overdue's active state uses the clay tint to echo the home OVERDUE
  // pill's color language.
  statusChipActiveClay: {
    backgroundColor: Palette.clay,
    borderColor: Palette.clay,
  },
  statusChipText: {
    fontFamily: Fonts.sansBold,
    fontSize: 12.5,
    color: Palette.inkSoft,
    letterSpacing: 0.3,
  },
  statusChipTextActive: {
    color: Palette.cream,
  },

  // ---- Search input -------------------------------------------------------
  searchWrap: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: Radius.pill,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  searchInput: {
    flex: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.ink,
    paddingVertical: 0,
  },

  // ---- Season chips (compact, 4-in-a-row) ---------------------------------
  // Lean on flex:1 to keep them equal-width and side-by-side on any
  // viewport. Removed the "done · to go" meta line + icon dot so the
  // chip stays small without sacrificing the headline pct and bar.
  seasonRow: {
    marginTop: 10,
    flexDirection: 'row',
    gap: 6,
  },
  seasonChip: {
    flex: 1,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    paddingHorizontal: 8,
    paddingVertical: 8,
    alignItems: 'center',
    gap: 2,
  },
  seasonBarTrack: {
    width: '100%',
    height: 3,
    borderRadius: 1.5,
    backgroundColor: Palette.sandDeep,
    overflow: 'hidden',
    marginTop: 4,
  },
  seasonBarFill: {
    height: '100%',
    borderRadius: 1.5,
  },
  seasonChipActive: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  seasonChipName: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  seasonChipNameActive: {
    color: 'rgba(251,246,234,0.85)',
  },
  seasonChipPct: {
    fontFamily: Fonts.serifBold,
    fontSize: 16,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  seasonChipPctActive: {
    color: Palette.cream,
  },
});
